import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PartyPopper, MapPin, FileSearch, ArrowRight, Home, ShieldCheck } from "lucide-react";

const euro = (n) => new Intl.NumberFormat("fr-FR").format(n) + " €";

export default function ThankYou() {
  const { state } = useLocation();
  const calc = state?.calc;
  const property = state?.property;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App min-h-screen bg-slate-50" data-testid="thank-you-page">
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="bg-white border border-slate-200 rounded-3xl shadow-sm p-8 sm:p-12 text-center"
        >
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="w-16 h-16 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center mb-6"
          >
            <PartyPopper className="w-8 h-8 text-emerald-500" strokeWidth={2} />
          </motion.div>

          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="thank-you-title">
            Merci ! Votre pré-estimation est prête
          </h1>

          {calc && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="mt-8 bg-slate-900 grain relative overflow-hidden rounded-3xl p-8 text-white"
              data-testid="thank-you-estimate"
            >
              {property && (
                <div className="text-sm text-slate-400 mb-2 capitalize">
                  {property.property_type} · {property.surface} m² · {property.city}
                </div>
              )}
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Votre fourchette de prix estimée</div>
              <div className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="thank-you-range">
                {euro(calc.estimate_low)} <span className="text-slate-500 font-normal">—</span> {euro(calc.estimate_high)}
              </div>
              <div className="mt-4 inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-xs font-medium text-slate-200">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Estimation gratuite &amp; 100% sans engagement
              </div>
            </motion.div>
          )}

          <p className="mt-6 text-base text-slate-600 leading-relaxed max-w-xl mx-auto" data-testid="thank-you-message">
            Un conseiller immobilier partenaire de votre secteur analyse actuellement les transactions
            notariées récentes dans votre rue pour affiner gratuitement votre avis de valeur.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4 text-left">
            <div className="flex items-start gap-3 bg-slate-50 rounded-2xl p-4">
              <div className="w-10 h-10 shrink-0 rounded-xl bg-blue-50 flex items-center justify-center">
                <FileSearch className="w-5 h-5 text-[#2563EB]" strokeWidth={2} />
              </div>
              <p className="text-sm text-slate-600">
                Analyse des ventes récentes comparables à votre bien.
              </p>
            </div>
            <div className="flex items-start gap-3 bg-slate-50 rounded-2xl p-4">
              <div className="w-10 h-10 shrink-0 rounded-xl bg-blue-50 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#2563EB]" strokeWidth={2} />
              </div>
              <p className="text-sm text-slate-600">
                Un expert local de votre secteur vous recontacte rapidement.
              </p>
            </div>
          </div>

          <Link
            to="/"
            data-testid="thank-you-home-btn"
            className="group inline-flex items-center gap-2 mt-10 bg-[#2563EB] text-white hover:bg-blue-700 px-7 py-4 rounded-xl font-semibold transition-all shadow-lg shadow-blue-500/20"
          >
            <Home className="w-5 h-5" strokeWidth={2} />
            Retour à l'accueil
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
          </Link>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}
